<template>
  <v-dialog :value="value" max-width="500" @input="$emit('input', $event)">
    <div class="refund-popup">
      <strong class="refund-popup__title">환불 신청</strong>

      <div v-if="overpayment" class="refund-popup__summary">
        <div class="refund-summary-row">
          <span class="refund-summary-row__label">납부일자</span>
          <span class="refund-summary-row__value">{{ formatDate(overpayment.approval_date) }}</span>
        </div>
        <div class="refund-summary-row">
          <span class="refund-summary-row__label">구분</span>
          <span class="refund-summary-row__value">{{ isRent ? overpayment.receipt_type : overpayment.yn_option }}</span>
        </div>
        <div class="refund-summary-row">
          <span class="refund-summary-row__label">환불 금액</span>
          <span class="refund-summary-row__value is-amount">{{ overpayment.refundable_amount | comma }}원</span>
        </div>
      </div>

      <div class="refund-popup__divider"></div>

      <div class="refund-popup__fields">
        <div class="refund-popup__field">
          <label>예금주명</label>
          <input
            v-model.trim="form.accountHolder"
            type="text"
            maxlength="30"
            placeholder="예금주명을 입력해주세요"
          />
          <p class="refund-popup__hint">
            계약자 본인 명의 계좌만 환불 신청이 가능합니다.
          </p>
        </div>

        <div class="refund-popup__field">
          <label>은행 선택</label>
          <div class="refund-popup__select-wrapper">
            <select v-model="form.bankCode">
              <option value="">은행을 선택해주세요</option>
              <option
                v-for="bank in bankList"
                :key="bank.code"
                :value="bank.code"
              >{{ bank.name }}</option>
            </select>
          </div>
        </div>

        <div class="refund-popup__field">
          <label>계좌번호</label>
          <input
            v-model.trim="form.accountNumber"
            type="text"
            maxlength="30"
            placeholder="'-' 없이 숫자만 입력해주세요"
            inputmode="numeric"
          />
        </div>

        <div class="refund-popup__field">
          <label>통장사본</label>
          <div class="refund-popup__file-row">
            <button
              type="button"
              class="refund-popup__file-name"
              :class="{ 'is-previewable': !!form.bankbookFile }"
              @click="openFileExternal"
            >
              {{ fileDisplayText }}
            </button>
            <button
              type="button"
              class="refund-popup__file-button"
              @click="$refs.fileInput.click()"
            >
              파일첨부
            </button>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="refund-popup__file-input"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
            @change="handleFileChange"
          />
          <p class="refund-popup__hint">
            PDF, 이미지 형식만 첨부할 수 있으며, 파일 크기는 10MB 이하만 가능합니다.
          </p>
        </div>
      </div>

      <div class="refund-popup__notice">
        <p>※ 입력하신 계좌 정보로 환불이 진행됩니다. 정확하게 입력해주세요.</p>
        <p>※ 환불 처리는 영업일 기준 3~5일이 소요될 수 있습니다.</p>
        <p>※ 환불 신청 후에는 특별한 사유 없이 재요청이 불가능하니 신중하게 신청해주세요.</p>
      </div>

      <div class="refund-popup__footer">
        <button
          type="button"
          class="refund-popup__button is-close"
          @click="goBack"
        >
          뒤로가기
        </button>
        <button
          type="button"
          class="refund-popup__button is-submit"
          :disabled="!isFormReady || loading"
          @click="submit"
        >
          {{ loading ? "신청 중..." : "환불 신청" }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="refund-loading-layer">
      <div class="refund-loading-dialog">
        <v-progress-circular indeterminate color="#ef761d" size="28"></v-progress-circular>
        <p>신청 중입니다...</p>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { getBankList } from "@/api/contract";

const MAX_UPLOAD_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_UPLOAD_FILE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "heic", "heif"];
const ALLOWED_UPLOAD_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
];

export default {
  name: "OverpaymentRefundDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    overpayment: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isRent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bankList: [],
      form: {
        accountHolder: "",
        bankCode: "",
        accountNumber: "",
        bankbookFile: null,
        bankbookFileName: "",
      },
    };
  },
  computed: {
    isFormReady() {
      return (
        this.form.accountHolder.length > 0 &&
        this.form.bankCode !== "" &&
        this.form.accountNumber.length > 0 &&
        this.form.bankbookFile !== null
      );
    },
    fileDisplayText() {
      return this.form.bankbookFileName || "첨부파일을 선택해주세요.";
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initForm();
      }
    },
  },
  created() {
    getBankList()
      .then((res) => {
        this.bankList = res.data;
      })
      .catch(() => {
        this.$store.commit("ERROR", "은행 목록을 불러오지 못했습니다.");
      });
  },
  methods: {
    formatDate(dt) {
      if (!dt || dt.length !== 8) return dt || "-";
      return `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}`;
    },
    initForm() {
      if (this.overpayment.refund_account_no) {
        this.form = {
          accountHolder: this.overpayment.refund_account_owner ?? "",
          bankCode: this.overpayment.refund_bank_code ?? "",
          bankName: this.bankList.find(b => b.code === this.overpayment.refund_bank_code)?.name || "",
          accountNumber: this.overpayment.refund_account_no ?? "",
          bankbookFile: null,
          bankbookFileName: "",
        };
      } else {
        this.resetForm();
      }
      
    },
    resetForm() {
      this.form = {
        accountHolder: "",
        bankCode: "",
        accountNumber: "",
        bankbookFile: null,
        bankbookFileName: "",
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    handleFileChange(event) {
      const [file] = event.target.files || [];
      if (!file) return;

      const error = this.validateUploadFile(file);
      if (error) {
        event.target.value = "";
        this.$store.commit("ERROR", error);
        return;
      }

      this.form.bankbookFile = file;
      this.form.bankbookFileName = file.name;
    },
    validateUploadFile(file) {
      const fileName = String(file?.name || "");
      const fileExtension = fileName.includes(".")
        ? fileName.split(".").pop().toLowerCase()
        : "";
      const fileType = String(file?.type || "").toLowerCase();

      if (!ALLOWED_UPLOAD_FILE_EXTENSIONS.includes(fileExtension)) {
        return "첨부파일은 PDF, 이미지 형식만 업로드할 수 있습니다.";
      }

      if (fileType && !ALLOWED_UPLOAD_FILE_TYPES.includes(fileType)) {
        return "첨부파일은 PDF, 이미지 형식만 업로드할 수 있습니다.";
      }

      if (Number(file?.size || 0) > MAX_UPLOAD_FILE_SIZE) {
        return "첨부파일은 10MB 이하만 업로드할 수 있습니다.";
      }

      return "";
    },
    openFileExternal() {
      if (!this.form.bankbookFile) return;

      if (typeof URL === "undefined" || !URL.createObjectURL) {
        this.$store.commit("ERROR", "첨부파일을 열 수 없습니다.");
        return;
      }

      const fileUrl = URL.createObjectURL(this.form.bankbookFile);
      const popup = window.open(fileUrl, "_blank");

      if (!popup) {
        this.$store.commit("ERROR", "첨부파일 창을 열 수 없습니다.");
      } else {
        popup.opener = null;
      }

      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
    },
    goBack() {
      this.$emit("back");
    },
    submit() {
      if (!this.isFormReady || this.loading) return;

      this.$emit("submit", {
        req_no: this.overpayment?.request_no,
        refund_amount: this.overpayment?.refundable_amount,
        owner: this.form.accountHolder,
        bank_code: this.form.bankCode,
        account_no: this.form.accountNumber,
        file: this.form.bankbookFile,
        ...(this.isRent ? { customer_no: this.overpayment?.customer_no } : {}),
      });
    },
  },
};
</script>

<style scoped>
.refund-popup {
  border-radius: 20px;
  background-color: #ffffff;
  padding: 28px 24px 22px;
  text-align: left;
}

.refund-popup__title {
  display: block;
  margin-bottom: 20px;
  text-align: center;
  color: #ef761d;
  font-size: 24px;
  font-weight: 700;
}

.refund-popup__summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 12px;
  background-color: #fafafa;
  padding: 14px 16px;
  margin-bottom: 18px;
}

.refund-summary-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.refund-summary-row__label {
  flex: 0 0 72px;
  color: #b8b8b8;
  font-size: 13px;
  font-weight: 700;
}

.refund-summary-row__value {
  color: #232323;
  font-size: 13px;
}

.refund-summary-row__value.is-amount {
  color: #ef761d;
  font-size: 15px;
  font-weight: 700;
}

.refund-popup__divider {
  height: 1px;
  background-color: #ececec;
  margin-bottom: 18px;
}

.refund-popup__fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.refund-popup__field label {
  display: block;
  margin-bottom: 8px;
  color: #b8b8b8;
  font-size: 14px;
  font-weight: 700;
}

.refund-popup__field input[type="text"] {
  width: 100%;
  height: 40px;
  border: 1px solid #252525;
  padding: 0 10px;
  font-size: 14px;
  color: #232323;
  box-sizing: border-box;
  border-radius: 6px;
}

/* 파일 첨부 — ContractorIdentityInfoChangeDialog와 동일한 패턴 */
.refund-popup__file-row {
  display: flex;
  gap: 8px;
}

.refund-popup__file-name {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid #222222;
  border-radius: 6px;
  padding: 10px 12px;
  color: #222222;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: #f7f7f7;
}

.refund-popup__file-name.is-previewable {
  cursor: pointer;
  text-decoration: underline;
}

.refund-popup__file-button {
  min-width: 80px;
  border: 0;
  border-radius: 6px;
  background-color: #000000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.refund-popup__file-input {
  display: none;
}

.refund-popup__hint {
  margin: 8px 0 0;
  color: #767676;
  font-size: 13px;
  line-height: 1.45;
  word-break: keep-all;
}

.refund-popup__select-wrapper {
  position: relative;
}

.refund-popup__select-wrapper::after {
  content: "";
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #444444;
  pointer-events: none;
}

.refund-popup__select-wrapper select {
  width: 100%;
  height: 40px;
  border: 1px solid #252525;
  border-radius: 6px;
  padding: 0 32px 0 10px;
  font-size: 14px;
  color: #232323;
  background-color: #ffffff;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
}

.refund-popup__select-wrapper select:invalid,
.refund-popup__select-wrapper select option[value=""] {
  color: #b8b8b8;
}

.refund-popup__notice {
  margin-bottom: 20px;
}

.refund-popup__notice p {
  margin: 0 0 6px;
  color: #ef761d;
  font-size: 12px;
  line-height: 1.5;
  word-break: keep-all;
}

.refund-popup__notice p:last-child {
  margin-bottom: 0;
}

.refund-popup__footer {
  display: flex;
  gap: 6px;
}

.refund-popup__button {
  height: 48px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.refund-popup__button.is-close {
  flex: 0 0 110px;
  background-color: #d9d9d9;
  color: #232323;
}

.refund-popup__button.is-submit {
  flex: 1 1 auto;
  background-color: #ef761d;
  color: #ffffff;
}

.refund-popup__button.is-submit:disabled {
  opacity: 0.45;
}

@media (max-width: 359px) {
  .refund-popup {
    padding: 24px 18px 18px;
  }

  .refund-popup__file-row {
    flex-direction: column;
    align-items: stretch;
  }
}

.refund-loading-layer {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refund-loading-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 28px 20px;
  border-radius: 18px;
  background-color: #ffffff;
}

.refund-loading-dialog p {
  margin: 0;
  color: #444444;
  font-size: 14px;
  font-weight: 700;
}
</style>
